/**
 * Hooks para gestión de eventos
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  eventService,
  eventRegistrationService,
  receiptService,
  eventPaymentService,
  adminRegistrationService,
  subscribeToEvent,
  subscribeToEventRegistrations,
  subscribeToEventPayments,
  subscribeToClubEventPayments,
  subscribeToClubEventPaymentsAll,
  subscribeToClubEventRanking,
  subscribeToEventRankings,
  subscribeToEvents,
  subscribeToUpcomingEvents,
  subscribeToEventsByStatus,
  subscribeToPublishedEvents,
  subscribeToEventsByOrganizer,
  subscribeToEventsByLevel,
  subscribeToClubRegistrations,
  subscribeToEventRegistrationsByStatus,
  subscribeToClubEventRegistrations,
  subscribeToPersonRegistrations,
  subscribeToPendingPaymentsByEvent,
} from "@/lib/services/events";
import type {
  Event,
  EventRegistration,
  ClubEventRanking,
  EventPayment,
  EventStatus,
  RegistrationStatus,
} from "@/types";
import { voidConsentBySwap } from "@/lib/services/eventConsents";
import { useAuthStore } from "@/stores/authStore";
import { useRealtimeList } from "@/hooks/useRealtime";

// ==================== QUERY KEYS ====================

export const eventKeys = {
  all: ["events"] as const,
  list: () => [...eventKeys.all, "list"] as const,
  byStatus: (status: EventStatus) =>
    [...eventKeys.all, "status", status] as const,
  published: () => [...eventKeys.all, "published"] as const,
  upcoming: () => [...eventKeys.all, "upcoming"] as const,
  byOrganizer: (organizerId: string) =>
    [...eventKeys.all, "organizer", organizerId] as const,
  byLevel: (level: Event["level"]) =>
    [...eventKeys.all, "level", level] as const,
  detail: (id: string) => [...eventKeys.all, id] as const,

  registrations: () => [...eventKeys.all, "registrations"] as const,
  registrationsByEvent: (eventId: string) =>
    [...eventKeys.registrations(), "event", eventId] as const,
  registrationsByClub: (clubId: string) =>
    [...eventKeys.registrations(), "club", clubId] as const,
  registrationsByClubAndEvent: (clubId: string, eventId: string) =>
    [...eventKeys.registrations(), "club", clubId, "event", eventId] as const,
  registrationsByPerson: (personId: string) =>
    [...eventKeys.registrations(), "person", personId] as const,
  registrationStats: (eventId: string) =>
    [...eventKeys.registrations(), "stats", eventId] as const,

  // Rankings
  rankings: () => [...eventKeys.all, "rankings"] as const,
  rankingsByEvent: (eventId: string) =>
    [...eventKeys.rankings(), "event", eventId] as const,
  rankingByClubAndEvent: (clubId: string, eventId: string) =>
    [...eventKeys.rankings(), "club", clubId, "event", eventId] as const,

  // Payments
  payments: () => [...eventKeys.all, "payments"] as const,
  paymentsByEvent: (eventId: string) =>
    [...eventKeys.payments(), "event", eventId] as const,
  paymentsByClubAndEvent: (clubId: string, eventId: string) =>
    [...eventKeys.payments(), "club", clubId, "event", eventId] as const,
  pendingPayments: () => [...eventKeys.payments(), "pending"] as const,
  pendingPaymentsByEvent: (eventId: string) =>
    [...eventKeys.payments(), "pending", eventId] as const,
};

// ==================== EVENT HOOKS ====================

export function useEvents() {
  return useRealtimeList<Event>(
    (cb, onErr) => subscribeToEvents(cb, onErr),
    [],
  );
}

export function useEventsByStatus(status: EventStatus | undefined) {
  return useRealtimeList<Event>(
    status ? (cb, onErr) => subscribeToEventsByStatus(status, cb, onErr) : null,
    [status],
  );
}

export function usePublishedEvents() {
  return useRealtimeList<Event>(
    (cb, onErr) => subscribeToPublishedEvents(cb, onErr),
    [],
  );
}

export function useUpcomingEvents() {
  return useRealtimeList<Event>(
    (cb, onErr) => subscribeToUpcomingEvents(cb, onErr),
    [],
  );
}

export function useEventsByOrganizer(organizerId: string | undefined) {
  return useRealtimeList<Event>(
    organizerId
      ? (cb, onErr) => subscribeToEventsByOrganizer(organizerId, cb, onErr)
      : null,
    [organizerId],
  );
}

export function useEventsByLevel(level: Event["level"] | undefined) {
  return useRealtimeList<Event>(
    level ? (cb, onErr) => subscribeToEventsByLevel(level, cb, onErr) : null,
    [level],
  );
}

export function useEvent(id: string | undefined) {
  const [data, setData] = useState<Event | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const unsub = subscribeToEvent(id, (event) => {
      setData(event);
      setIsLoading(false);
    });
    return unsub;
  }, [id]);

  return { data, isLoading };
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Event,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => eventService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      toast.success("Evento creado correctamente");
    },
    onError: () => {
      toast.error("Error al crear el evento");
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Event> }) =>
      eventService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      toast.success("Evento actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el evento");
    },
  });
}

export function useUpdateEventStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: EventStatus }) =>
      eventService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      toast.success("Estado del evento actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el estado");
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eventService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      toast.success("Evento eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el evento");
    },
  });
}

// ==================== REGISTRATION HOOKS ====================

export function useEventRegistrations(eventId: string | undefined) {
  const [data, setData] = useState<EventRegistration[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!eventId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeToEventRegistrations(
      eventId,
      (registrations) => {
        setData(registrations);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [eventId]);

  return { data, isLoading, error };
}

export function useEventRegistrationsByStatus(
  eventId: string | undefined,
  status: RegistrationStatus,
) {
  return useRealtimeList<EventRegistration>(
    eventId
      ? (cb, onErr) =>
          subscribeToEventRegistrationsByStatus(eventId, status, cb, onErr)
      : null,
    [eventId, status],
  );
}

export function useClubRegistrations(clubId: string | undefined) {
  return useRealtimeList<EventRegistration>(
    clubId
      ? (cb, onErr) => subscribeToClubRegistrations(clubId, cb, onErr)
      : null,
    [clubId],
  );
}

export function useClubEventRegistrations(
  clubId: string | undefined,
  eventId: string | undefined,
) {
  return useRealtimeList<EventRegistration>(
    clubId && eventId
      ? (cb, onErr) =>
          subscribeToClubEventRegistrations(clubId, eventId, cb, onErr)
      : null,
    [clubId, eventId],
  );
}

export function usePersonRegistrations(personId: string | undefined) {
  return useRealtimeList<EventRegistration>(
    personId
      ? (cb, onErr) => subscribeToPersonRegistrations(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useEventStats(eventId: string | undefined) {
  return useQuery({
    queryKey: eventKeys.registrationStats(eventId!),
    queryFn: () => eventRegistrationService.getEventStats(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateEventRegistration() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      variables: Omit<
        EventRegistration,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      > & {
        /** Sólo admins: omite las validaciones de fecha de inscripción. */
        skipDateChecks?: boolean;
      },
    ) => {
      const { skipDateChecks, ...data } = variables;
      return eventRegistrationService.create(data, user?.uid || "", {
        skipDateChecks,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByEvent(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByClub(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.detail(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationStats(variables.eventId),
      });
      // Nota: no mostramos toast aquí porque este hook se usa en flujos
      // masivos (inscribir N miembros) donde el caller emite un único toast
      // resumen al finalizar. Evita spam de notificaciones.
    },
    onError: () => {
      // Mismo motivo: el caller decide cómo reportar errores agregados.
    },
  });
}

export function useUpdateEventRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<EventRegistration>;
      eventId: string;
    }) => eventRegistrationService.update(id, data),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationStats(eventId),
      });
    },
    onError: () => {
      toast.error("Error al actualizar la inscripción");
    },
  });
}

export function useUpdateRegistrationStatus() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: ({
      id,
      status,
      rejectionReason,
    }: {
      id: string;
      status: RegistrationStatus;
      eventId: string;
      rejectionReason?: string;
    }) =>
      eventRegistrationService.updateStatus(
        id,
        status,
        user?.uid,
        rejectionReason,
      ),
    onSuccess: (_, { eventId, status }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationStats(eventId),
      });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });

      const messages: Record<RegistrationStatus, string> = {
        pending: "Inscripción marcada como pendiente",
        approved: "Inscripción aprobada",
        rejected: "Inscripción rechazada",
        cancelled: "Inscripción cancelada",
        waitlist: "Inscripción en lista de espera",
      };
      toast.success(messages[status]);
    },
    onError: () => {
      toast.error("Error al actualizar el estado");
    },
  });
}

export function useCancelEventRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; eventId: string }) =>
      eventRegistrationService.cancel(id),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationStats(eventId),
      });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });
      toast.success("Inscripción cancelada");
    },
    onError: () => {
      toast.error("Error al cancelar la inscripción");
    },
  });
}

// ==================== RANKING HOOKS ====================

export function useEventRankings(eventId: string | undefined) {
  const [data, setData] = useState<ClubEventRanking[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const unsub = subscribeToEventRankings(eventId, (rankings) => {
      setData(rankings);
      setIsLoading(false);
    });
    return unsub;
  }, [eventId]);

  return { data, isLoading };
}

export function useClubEventRanking(
  clubId: string | undefined,
  eventId: string | undefined,
) {
  const [data, setData] = useState<ClubEventRanking | null | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!clubId || !eventId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const unsub = subscribeToClubEventRanking(clubId, eventId, (ranking) => {
      setData(ranking);
      setIsLoading(false);
    });
    return unsub;
  }, [clubId, eventId]);

  return { data, isLoading };
}

// ==================== RECEIPT HOOKS ====================

export function useUploadReceipt() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      registrationId,
      file,
    }: {
      registrationId: string;
      file: File;
      eventId: string;
      clubId: string;
    }) => receiptService.uploadReceipt(registrationId, file, user?.uid || ""),
    onSuccess: (_, { eventId, clubId }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.registrationsByClubAndEvent(clubId, eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingByClubAndEvent(clubId, eventId),
      });
      toast.success("Recibo subido exitosamente");
    },
    onError: () => {
      toast.error("Error al subir el recibo");
    },
  });
}

// ==================== PAYMENT HOOKS ====================

export function useEventPayments(eventId: string | undefined) {
  const [data, setData] = useState<EventPayment[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!eventId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeToEventPayments(
      eventId,
      (payments) => {
        setData(payments);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [eventId]);

  return { data, isLoading, error };
}

export function useClubEventPayments(
  clubId: string | undefined,
  eventId: string | undefined,
) {
  const [data, setData] = useState<EventPayment[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!clubId || !eventId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeToClubEventPayments(
      clubId,
      eventId,
      (payments) => {
        setData(payments);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [clubId, eventId]);

  return { data, isLoading, error };
}

export function usePendingPaymentsByEvent(eventId: string | undefined) {
  return useRealtimeList<EventPayment>(
    eventId
      ? (cb, onErr) => subscribeToPendingPaymentsByEvent(eventId, cb, onErr)
      : null,
    [eventId],
  );
}

export function useClubEventPaymentsAll(clubId: string | undefined) {
  return useRealtimeList<EventPayment>(
    clubId
      ? (cb, onErr) => subscribeToClubEventPaymentsAll(clubId, cb, onErr)
      : null,
    [clubId],
  );
}

export function useUploadPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      eventId,
      eventName,
      clubId,
      clubName,
      amount,
      file,
      description,
      notes,
    }: {
      eventId: string;
      eventName: string;
      clubId: string;
      clubName: string;
      amount: number;
      file: File;
      description?: string;
      notes?: string;
    }) =>
      eventPaymentService.uploadPayment(
        eventId,
        eventName,
        clubId,
        clubName,
        amount,
        file,
        user?.uid || "",
        user?.displayName || "Director",
        description,
        notes,
      ),
    onSuccess: (_, { eventId, clubId }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.paymentsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.paymentsByClubAndEvent(clubId, eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingByClubAndEvent(clubId, eventId),
      });
      toast.success("Pago subido exitosamente");
    },
    onError: (error) => {
      console.error("Error al subir el pago:", error);
      toast.error("Error al subir el pago");
    },
  });
}

export function useRecordCashPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      eventId,
      eventName,
      clubId,
      clubName,
      amount,
      description,
      notes,
    }: {
      eventId: string;
      eventName: string;
      clubId: string;
      clubName: string;
      amount: number;
      description?: string;
      notes?: string;
    }) =>
      eventPaymentService.recordCashPayment(
        eventId,
        eventName,
        clubId,
        clubName,
        amount,
        user?.uid || "",
        user?.displayName || "Admin",
        description,
        notes,
      ),
    onSuccess: (_, { eventId, clubId }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.paymentsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.paymentsByClubAndEvent(clubId, eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.rankingByClubAndEvent(clubId, eventId),
      });
      toast.success("Pago en efectivo registrado");
    },
    onError: (error) => {
      console.error("Error al registrar pago en efectivo:", error);
      toast.error("Error al registrar pago en efectivo");
    },
  });
}

export function useValidatePayment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      paymentId,
      status,
      rejectionReason,
      newAmount,
    }: {
      paymentId: string;
      status: "approved" | "rejected" | "pending";
      eventId: string;
      rejectionReason?: string;
      /** Si se pasa, el admin corrigió el monto antes de aprobar. */
      newAmount?: number;
    }) =>
      eventPaymentService.validatePayment(
        paymentId,
        status,
        user?.uid || "",
        user?.displayName || "Admin",
        rejectionReason,
        newAmount,
      ),
    onSuccess: (_, { eventId, status, newAmount }) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.paymentsByEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventKeys.pendingPaymentsByEvent(eventId),
      });
      const adjusted = typeof newAmount === "number";
      toast.success(
        status === "approved"
          ? adjusted
            ? "Pago aprobado con monto corregido"
            : "Pago aprobado"
          : status === "rejected"
            ? "Pago rechazado"
            : "Validación revertida — el pago vuelve a pendiente",
      );
    },
    onError: () => {
      toast.error("Error al validar el pago");
    },
  });
}

// ==================== ADMIN MUTATIONS ====================

function useActor() {
  const user = useAuthStore((s) => s.user);
  return {
    uid: user?.uid || "",
    name: user?.displayName || user?.email || undefined,
  };
}

function invalidateAfterAdminOp(
  queryClient: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  queryClient.invalidateQueries({
    queryKey: eventKeys.registrationsByEvent(eventId),
  });
  queryClient.invalidateQueries({
    queryKey: eventKeys.registrationStats(eventId),
  });
  queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });
}

/**
 * Intercambia un integrante por otro dentro del mismo club (cambio de última
 * hora). El que entra hereda precio y estado de pago del que sale. Además
 * invalida el consentimiento del que sale (el del que entra es nuevo y se
 * pre-crea desde la UI tras el éxito).
 */
export function useSwapRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: async ({
      outgoingRegistrationId,
      eventId,
      outgoingMemberId,
      incoming,
      reason,
    }: {
      outgoingRegistrationId: string;
      eventId: string;
      /** memberId del que SALE — para invalidar su consentimiento. */
      outgoingMemberId?: string;
      incoming: { personId: string; personName: string; memberId: string };
      reason?: string;
    }) => {
      const result = await eventRegistrationService.swap({
        outgoingRegistrationId,
        incoming,
        actor,
        reason,
      });
      // Invalidar el consentimiento del que sale (no bloquea el resultado).
      if (outgoingMemberId) {
        try {
          await voidConsentBySwap(eventId, outgoingMemberId, actor.uid);
        } catch (e) {
          console.error("[useSwapRegistration] voidConsentBySwap falló", e);
        }
      }
      return result;
    },
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Integrante intercambiado correctamente");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al intercambiar el integrante");
    },
  });
}

export function useAdminEditRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: ({
      registrationId,
      patch,
      reason,
    }: {
      registrationId: string;
      eventId: string;
      patch: Parameters<typeof adminRegistrationService.editFields>[1];
      reason?: string;
    }) =>
      adminRegistrationService.editFields(registrationId, patch, actor, reason),
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Inscripción actualizada");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al actualizar la inscripción");
    },
  });
}

export function useAdminTransferRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: ({
      registrationId,
      targetClubId,
      targetClubName,
      reason,
    }: {
      registrationId: string;
      eventId: string;
      targetClubId: string;
      targetClubName: string;
      reason?: string;
    }) =>
      adminRegistrationService.transferToClub(
        registrationId,
        targetClubId,
        targetClubName,
        actor,
        reason,
      ),
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Inscripción transferida");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al transferir la inscripción");
    },
  });
}

export function useAdminForceStatusRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: ({
      registrationId,
      status,
      reason,
    }: {
      registrationId: string;
      eventId: string;
      status: RegistrationStatus;
      reason?: string;
    }) =>
      adminRegistrationService.forceStatus(
        registrationId,
        status,
        actor,
        reason,
      ),
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Estado actualizado");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al cambiar el estado");
    },
  });
}

export function useAdminReactivateRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: ({
      registrationId,
      reason,
    }: {
      registrationId: string;
      eventId: string;
      reason?: string;
    }) => adminRegistrationService.reactivate(registrationId, actor, reason),
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Inscripción reactivada");
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo reactivar");
    },
  });
}

export function useAdminDeleteRegistration() {
  const queryClient = useQueryClient();
  const actor = useActor();
  return useMutation({
    mutationFn: ({
      registrationId,
      reason,
    }: {
      registrationId: string;
      eventId: string;
      reason?: string;
    }) => adminRegistrationService.hardDelete(registrationId, actor, reason),
    onSuccess: (_, { eventId }) => {
      invalidateAfterAdminOp(queryClient, eventId);
      toast.success("Inscripción eliminada");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al eliminar");
    },
  });
}
